import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Breadcrumb from 'components/ui/Breadcrumb';
import JobApplicationForm from './components/JobApplicationForm';


import ShareJobModal from './components/ShareJobModal';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useAIAgent } from '@/contexts/AIAgentContext';
import { Button } from '@/components/ui/Button'; // Import Button explicitly if needed or use existing HTML button classes

const JobDetailApplication = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { isActive, agentState, selectJobForAgent, confirmJobStart, currentJob } = useAIAgent();
  const navigate = useNavigate();

  const [jobData, setJobData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplicationFormOpen, setIsApplicationFormOpen] = useState(false);
  const [isJobSaved, setIsJobSaved] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  useEffect(() => {
    const fetchJobData = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select('*, companies(*)')
          .eq('id', id)
          .single();

        if (error) throw error;

        // Transform to match component expectations
        setJobData({
          ...data,
          salary: `£${(data.salary_min / 1000).toFixed(0)}k - £${(data.salary_max / 1000).toFixed(0)}k`,
          company: {
            name: data.companies?.name,
            logo: data.companies?.logo_url,
            size: data.companies?.size,
            website: data.companies?.website,
            description: data.companies?.description,
            industry: 'Technology', // Defaulting since it's not in schema yet
            culturePhotos: [] // Defaulting
          },
          postedDate: data.posted_at,
          applicationDeadline: new Date(new Date(data.posted_at).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          responsibilities: [
            "Maintain and scale the application infrastructure",
            "Collaborate with team members to implement new features",
            "Ensure high quality code through testing and reviews"
          ],
          preferredQualifications: [
            "Previous experience in a similar role",
            "Familiarity with the tech stack",
            "Strong communication skills"
          ],
          benefits: [
            "Competitive salary",
            "Flexible working hours",
            "Professional growth opportunities"
          ],
          applicationQuestions: [
            { id: 1, question: "What interests you most about this role?", type: "textarea", required: true },
            { id: 2, question: "Describe your experience with our tech stack", type: "textarea", required: true }
          ]
        });

        // Check if job is saved
        if (user) {
          const { data: savedData } = await supabase
            .from('saved_jobs')
            .select('id')
            .eq('user_id', user.id)
            .eq('job_id', id)
            .single();

          setIsJobSaved(!!savedData);
        }
      } catch (err) {
        console.error('Error fetching job details:', err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobData();
  }, [id, user]);

  // AI Agent Logic
  useEffect(() => {
    // If agent is active and selected this job, open the form automatically appropriately
    if (isActive && currentJob?.id === jobData?.id && agentState === 'analyzing' && !isApplicationFormOpen) {
      setIsApplicationFormOpen(true);
    }
  }, [isActive, currentJob, jobData, agentState, isApplicationFormOpen]);

  const handleAgentStart = () => {
    if (jobData) {
      selectJobForAgent(jobData);
    }
  };



  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Browse Jobs', path: '/job-search-browse' },
    { label: jobData?.title || 'Job Detail', path: '#', isLast: true }
  ];

  const handleSaveJob = async () => {
    if (!user) {
      navigate('/job-seeker-registration-login');
      return;
    }

    try {
      if (isJobSaved) {
        await supabase.from('saved_jobs').delete().eq('user_id', user.id).eq('job_id', id);
        setIsJobSaved(false);
      } else {
        await supabase.from('saved_jobs').insert({ user_id: user.id, job_id: id });
        setIsJobSaved(true);
      }
    } catch (err) {
      console.error('Error saving job:', err.message);
    }
  };

  const handleApplyNow = () => {
    if (!user) {
      navigate('/job-seeker-registration-login');
      return;
    }
    setIsApplicationFormOpen(true);
  };

  const handleShareJob = () => {
    setIsShareModalOpen(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysAgo = (dateString) => {
    const posted = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - posted);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Icon name="Loader2" size={48} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-4">Job not found</h2>
        <Link to="/job-search-browse" className="btn-primary">Back to browsing</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb customItems={breadcrumbItems} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Job Header */}
            <div className="bg-background rounded-lg shadow-soft border border-border p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Image
                      src={jobData.company.logo}
                      alt={`${jobData.company.name} logo`}
                      className="w-16 h-16 rounded-lg object-cover border border-border"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2">
                      {jobData.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 text-text-secondary mb-3">
                      <Link
                        to="/company-registration-profile-setup"
                        className="text-lg font-semibold text-primary-600 hover:text-primary-700 transition-smooth"
                      >
                        {jobData.company.name}
                      </Link>
                      <div className="flex items-center space-x-1">
                        <Icon name="MapPin" size={16} />
                        <span>{jobData.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span>{jobData.salary}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-text-muted">
                      <span className="flex items-center space-x-1">
                        <Icon name="Clock" size={14} />
                        <span>Posted {getDaysAgo(jobData.postedDate)} days ago</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="Briefcase" size={14} />
                        <span>{jobData.employmentType}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Icon name="TrendingUp" size={14} />
                        <span>{jobData.experienceLevel}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleApplyNow}
                  className="btn-primary flex-1 sm:flex-none sm:px-8 py-3 text-base font-semibold flex items-center justify-center whitespace-nowrap"
                >
                  <Icon name="Send" size={20} className="mr-2" />
                  Apply Now
                </button>
                <button
                  onClick={handleSaveJob}
                  className={`btn-secondary flex items-center justify-center px-4 py-3 ${isJobSaved ? 'bg-accent-50 text-accent-600 border-accent-200' : ''
                    }`}
                >
                  <Icon name={isJobSaved ? "BookmarkCheck" : "Bookmark"} size={20} />
                  <span className="ml-2 sm:hidden">{isJobSaved ? 'Saved' : 'Save Job'}</span>
                </button>
                <button
                  onClick={handleShareJob}
                  className="btn-secondary flex items-center justify-center px-4 py-3"
                >
                  <Icon name="Share2" size={20} />
                  <span className="ml-2 sm:hidden">Share</span>
                </button>
              </div>
            </div>

            {/* Job Description */}
            <div className="bg-background rounded-lg shadow-soft border border-border p-6 mb-6">
              <h2 className="text-xl font-semibold text-text-primary mb-4">Job Description</h2>
              <div className="prose prose-lg max-w-none text-text-secondary mb-6">
                <p>{jobData.description}</p>
              </div>

              <div className="space-y-6">
                {/* Responsibilities */}


                {/* Requirements */}

              </div>
            </div>

            {/* Application Deadline */}
            <div className="bg-warning-50 dark:bg-amber-950/30 border border-warning-200 dark:border-amber-900/50 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <Icon name="Calendar" size={20} className="text-warning-600 dark:text-amber-500" />
                <div>
                  <p className="text-warning-800 dark:text-amber-200 font-medium">Application Deadline</p>
                  <p className="text-warning-700 dark:text-amber-300 text-sm">
                    Applications close on {formatDate(jobData.applicationDeadline)}
                  </p>
                </div>
              </div>
            </div>


          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Sticky Application Panel - Mobile */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 z-1030">
              <div className="flex space-x-3">
                <button
                  onClick={handleApplyNow}
                  className="btn-primary flex-1 py-3 text-base font-semibold"
                >
                  Apply Now
                </button>
                <button
                  onClick={handleSaveJob}
                  className={`btn-secondary px-4 py-3 ${isJobSaved ? 'bg-accent-50 text-accent-600 border-accent-200' : ''
                    }`}
                >
                  <Icon name={isJobSaved ? "BookmarkCheck" : "Bookmark"} size={20} />
                </button>
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* Application Form Modal */}
      {isApplicationFormOpen && (
        <JobApplicationForm
          job={jobData}
          onClose={() => setIsApplicationFormOpen(false)}
        />
      )}

      {/* Share Modal */}
      {isShareModalOpen && (
        <ShareJobModal
          job={jobData}
          onClose={() => setIsShareModalOpen(false)}
        />
      )}

      {/* Mobile bottom padding */}
      <div className="lg:hidden h-20"></div>

      {/* AI Agent Selection Prompt */}
      {isActive && agentState === 'selecting' && (
        <div className="fixed inset-0 z-1050 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-md w-full p-6 shadow-2xl border-2 border-violet-500 animate-in fade-in zoom-in duration-300">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg text-violet-600 dark:text-violet-400">
                <Icon name="Bot" size={24} />
              </div>
              <h3 className="text-xl font-bold">Apply with AI Agent?</h3>
            </div>
            <p className="text-neutral-600 dark:text-neutral-300 mb-6">
              I can help you apply to <span className="font-semibold text-neutral-900 dark:text-white">{jobData?.title}</span>.
              I'll auto-fill your details and help write your cover letter.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={handleAgentStart}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white"
              >
                Yes, Start Application
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AI Agent Confirmation Prompt */}
      {isActive && agentState === 'confirming_job' && currentJob?.id === jobData?.id && (
        <div className="fixed inset-0 z-1050 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-md w-full p-6 shadow-2xl border-2 border-violet-500">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg text-violet-600 dark:text-violet-400">
                <Icon name="Bot" size={24} />
              </div>
              <h3 className="text-xl font-bold">Ready to Start?</h3>
            </div>
            <p className="text-neutral-600 dark:text-neutral-300 mb-6">
              I'm ready to analyze this application and guide you.
            </p>
            <Button
              onClick={confirmJobStart}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white"
            >
              Let's Go
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailApplication;